import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';

import { useGetDashboardDataByWorkspace } from '@/api/dashboard/useDashboardDetailsByWorkspace';
import { Ticket, useGetTickets } from '@/api/tickets/useGetTickets';
import Loader from '@/component-library/primitives/Loader/Loader';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import Table from '@/component-library/widgets/Table/Table';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import TicketDetailModal, { type TicketDetailTicket } from '@/modules/Dashboard/Tickets/TicketDetailModal';

import { extractChurnSignalsFromDashboard, filterSignalsForMember } from './memberProfileChurnUtils';

interface MemberTicketsTabProps {
  memberData: MemberOverview | undefined;
}

/** .NET default JSON uses numeric enums; strings are still possible if serialization changes. */
const TICKET_PRIORITY_LABELS = ['low', 'medium', 'high', 'critical'] as const;
const TICKET_STATUS_LABELS = ['open', 'in progress', 'resolved', 'closed'] as const;

function formatTicketPriority(value: unknown): string {
  if (typeof value === 'number' && value >= 0 && value < TICKET_PRIORITY_LABELS.length) {
    return TICKET_PRIORITY_LABELS[value];
  }
  if (typeof value === 'string') return value.replace(/_/g, ' ');
  return String(value ?? '');
}

function formatTicketStatus(value: unknown): string {
  if (typeof value === 'number' && value >= 0 && value < TICKET_STATUS_LABELS.length) {
    return TICKET_STATUS_LABELS[value];
  }
  if (typeof value === 'string') return value.replace(/_/g, ' ');
  return String(value ?? '');
}

function metadataSignalId(meta: Record<string, unknown> | undefined): string | undefined {
  if (!meta) return undefined;
  const v = meta.signalId ?? meta.SignalId;
  if (v == null) return undefined;
  return String(v).trim();
}

/** Tickets linked to this member: explicit `audienceLinks`, API `metadata.signalId`, or legacy churnSignalData / description. */
function filterTicketsForMember(
  tickets: Ticket[],
  memberId: string | undefined,
  memberEmail: string | undefined,
  memberSignalIdsLower: Set<string>,
): Ticket[] {
  const email = memberEmail?.trim().toLowerCase();
  const canMatchEmail = Boolean(email);
  const memberIdLower = memberId?.trim().toLowerCase();

  return tickets.filter((ticket) => {
    const links = ticket.audienceLinks;
    if (memberIdLower && links?.length) {
      const hit = links.some((l) => String(l.audienceMemberId ?? '').toLowerCase() === memberIdLower);
      if (hit) return true;
    }

    const sid = metadataSignalId(ticket.metadata);
    if (sid && memberSignalIdsLower.has(sid.toLowerCase())) return true;

    if (canMatchEmail) {
      const cs = ticket.churnSignalData;
      if (cs?.userEmail?.trim().toLowerCase() === email) return true;

      const desc = (ticket.description || '').toLowerCase();
      if (desc.includes(`(${email})`)) return true;
    }

    return false;
  });
}

const MemberTicketsTab: React.FC<MemberTicketsTabProps> = ({ memberData }) => {
  const { t } = useTranslation();
  const [ticketForModal, setTicketForModal] = useState<TicketDetailTicket | null>(null);
  const { data: ticketsResponse, isLoading: ticketsLoading } = useGetTickets({ includeArchived: true, limit: 200 });

  const { startTime, endTime } = useMemo(() => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return { startTime: startDate.toISOString(), endTime: endDate.toISOString() };
  }, []);

  const { data: dashboardData, isLoading: dashboardLoading } = useGetDashboardDataByWorkspace(startTime, endTime);

  const memberSignalIds = useMemo(() => {
    const all = extractChurnSignalsFromDashboard(dashboardData);
    const mine = filterSignalsForMember(all, memberData?.id, memberData?.email);
    return new Set(mine.map((s) => String(s.id).trim().toLowerCase()));
  }, [dashboardData, memberData?.id, memberData?.email]);

  const memberTickets = useMemo(() => {
    const list = ticketsResponse?.tickets ?? [];
    const filtered = filterTicketsForMember(list, memberData?.id, memberData?.email, memberSignalIds);
    // API returns ticket PK as `id` only; Table row navigation keys off `ticketId` (see Tickets.tsx extractTicketId).
    return filtered.map((t) => ({
      ...t,
      ticketId: t.ticketId || t.id || '',
    })) as TicketDetailTicket[];
  }, [ticketsResponse?.tickets, memberData?.id, memberData?.email, memberSignalIds]);

  const columnHelper = createColumnHelper<TicketDetailTicket>();

  const columns = [
    columnHelper.accessor('title', {
      header: () => (
        <div className="text-custom-black font-medium">{t('MemberProfilePage.ticketTitleColumn', 'Ticket')}</div>
      ),
      cell: (info) => <div className="text-sm font-medium text-custom-black">{info.renderValue()}</div>,
    }),
    columnHelper.accessor('priority', {
      header: () => (
        <div className="text-custom-black font-medium">{t('MemberProfilePage.priorityColumnLabel', 'Priority')}</div>
      ),
      cell: (info) => <span className="text-xs capitalize">{formatTicketPriority(info.getValue())}</span>,
    }),
    columnHelper.accessor('status', {
      header: () => (
        <div className="text-custom-black font-medium">{t('MemberProfilePage.statusColumnLabel', 'Status')}</div>
      ),
      cell: (info) => <span className="text-xs capitalize">{formatTicketStatus(info.getValue())}</span>,
    }),
    columnHelper.accessor('createdAt', {
      header: () => (
        <div className="text-custom-black font-medium">{t('MemberProfilePage.createdColumnLabel', 'Created')}</div>
      ),
      cell: (info) => (
        <span className="text-sm text-custom-darkSilver">
          {info.renderValue() ? new Date(info.renderValue() as string).toLocaleString() : '—'}
        </span>
      ),
    }),
  ];

  const loading = ticketsLoading || dashboardLoading;

  return (
    <div className="flex flex-col h-full">
      <p className="text-sm text-custom-darkSilver mb-4">
        {t(
          'MemberProfilePage.memberTicketsBlurb',
          'Tickets linked to this person (related members), from churn signals, or mentioning their email (last 30 days for signal links).',
        )}
      </p>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : memberTickets.length > 0 ? (
        <>
          <Table columns={columns} data={memberTickets} onRowClick={(row) => setTicketForModal(row)} />
          <TicketDetailModal
            open={ticketForModal != null}
            ticket={ticketForModal}
            onClose={() => setTicketForModal(null)}
          />
        </>
      ) : (
        <NoDataBoard description={t('MemberProfilePage.memberTicketsEmpty', 'No tickets linked to this person yet.')} />
      )}
    </div>
  );
};

export default MemberTicketsTab;
